import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";

import { OVERLAY_PREF } from "../constants";
import {
  buildFieldLayout,
  findFlatNeighbor,
  findVerticalNeighbor,
  measureField,
  type NavField,
} from "./fieldNavigation";

interface ILogbookRefsContext {
  getInputRefProps: (id: string) => {
    ref: (ref: any) => void;
    onKeyDown: (event: any) => void;
  };
}

const LogbookRefsContext = createContext<ILogbookRefsContext | null>(null);

export const LogbookRefsProvider = ({ children }: { children: ReactNode }) => {
  const inputRefs = useRef<Record<string, any>>({});

  useEffect(() => {
    inputRefs.current = {};
  }, []);

  const addInputRef = (id: string, ref: any) => {
    if (!id.includes(OVERLAY_PREF)) {
      if (ref) {
        inputRefs.current[id] = ref;
      } else {
        // Clean up null refs so stale entries don't accumulate
        delete inputRefs.current[id];
      }
    }
  };

  const handleKeyDown = (event: any, id: string) => {
    const key = event?.key;
    if (key !== "Tab" && key !== "Enter") return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.defaultPrevented)
      return;

    // With a dropdown open, Enter picks the highlighted option
    if (
      key === "Enter" &&
      event.target?.getAttribute?.("aria-expanded") === "true"
    ) {
      return;
    }

    const fields = buildFieldLayout(Object.entries(inputRefs.current));

    // Prefer the event's element, so cells that take the handler without
    // registering a ref can still navigate
    let sourceIndex = fields.findIndex(field => field.element === event.target);
    if (sourceIndex === -1 && inputRefs.current[id]) {
      const registered = inputRefs.current[id];
      sourceIndex = fields.findIndex(field => field.element === registered);
    }

    const source: NavField | null =
      sourceIndex !== -1 ? fields[sourceIndex] : measureField(id, event.target);
    if (!source) return;

    let next: NavField | null = null;
    if (key === "Tab") {
      // An unregistered source has no place in the flat order, so let the
      // browser handle Tab rather than trapping focus
      next =
        sourceIndex === -1
          ? null
          : findFlatNeighbor(fields, sourceIndex, event.shiftKey ? -1 : 1);
    } else {
      next = findVerticalNeighbor(
        fields,
        source,
        event.shiftKey ? "Up" : "Down",
      );
    }

    if (!next?.element || !("focus" in next.element)) return;

    event.preventDefault();
    next.element.focus();
  };

  const getInputRefProps = useCallback(
    (id: string) => ({
      ref: (ref: any) => addInputRef(id, ref),
      onKeyDown: (e: any) => handleKeyDown(e, id),
    }),
    [],
  );

  return (
    <LogbookRefsContext.Provider value={{ getInputRefProps }}>
      {children}
    </LogbookRefsContext.Provider>
  );
};

export const useLogbookRefsContext = (id: string) => {
  const context = useContext(LogbookRefsContext);
  if (!context) {
    throw new Error(
      "useLogbookRefsContext must be used within a LogbookRefsProvider",
    );
  }
  return context.getInputRefProps(id);
};
